'use client';
import { readCookieHeader, writePreferenceCookie } from '../cookies';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { dictionaries } from './dictionaries';
import { DEFAULT_LOCALE, isLocale, type Locale } from './locale';
import type { LocaleDict } from './types';
import { elowenClient } from '../elowenClient';

const STORAGE_KEY = 'elowen-locale';
function readLocaleCookie(): Locale | null {
  const value = readCookieHeader(document.cookie, STORAGE_KEY);
  return isLocale(value) ? value : null;
}

interface LangContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: LocaleDict;
}

const LangContext = createContext<LangContextValue | null>(null);

/** `initialLocale` is what the SERVER rendered this document in (from the cookie). Starting state must
 *  match it exactly or hydration would mismatch — and starting from anything else is what made the
 *  whole interface repaint in another language a moment after it appeared. */
export function LanguageProvider({ children, initialLocale = DEFAULT_LOCALE, syncServer = false, onServerLocaleChanged }: { children: ReactNode; initialLocale?: Locale; syncServer?: boolean; onServerLocaleChanged?: () => void }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    // localStorage remains the client's source of truth, so a session that predates the cookie — or one
    // whose cookie was cleared — still gets its stored choice, and writing the cookie back means the
    // NEXT document is already correct on the server. Only a genuine disagreement re-renders.
    const stored = localStorage.getItem(STORAGE_KEY);
    const resolved = isLocale(stored) ? stored : initialLocale;
    if (resolved !== initialLocale) setLocaleState(resolved);
    if (!isLocale(stored)) localStorage.setItem(STORAGE_KEY, resolved);
    if (readLocaleCookie() !== resolved) writePreferenceCookie(STORAGE_KEY, resolved);
    document.documentElement.lang = resolved;
    if (syncServer) {
      void elowenClient.myLocale()
        .then((stored) => stored.locale !== resolved
          ? elowenClient.saveMyLocale(resolved, stored.revision)
          : undefined)
        .then(saved => { if (saved) onServerLocaleChanged?.(); })
        .catch((error: unknown) => console.error('Failed to reconcile the account locale', error));
    }
  }, [initialLocale, syncServer, onServerLocaleChanged]);

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
    writePreferenceCookie(STORAGE_KEY, l);
    document.documentElement.lang = l;
    if (syncServer) {
      void elowenClient.saveMyLocale(l).then(() => onServerLocaleChanged?.()).catch((error: unknown) => {
        console.error('Failed to save the account locale', error);
      });
    }
  };

  return (
    <LangContext.Provider value={{ locale, setLocale, t: dictionaries[locale] }}>
      {children}
    </LangContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useTranslation must be used within LanguageProvider');
  return ctx;
}

/** Locale for isolated shared components rendered without a language provider. */
export function useLocaleSafe(): Locale {
  return useContext(LangContext)?.locale ?? DEFAULT_LOCALE;
}
